import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { Card } from 'tea-component';
import AnswerIcon from '../img/answer-icon.svg';
import Salute from '../img/agent_salute.png';
import BasicImage from '@home/components/BasicImage';

interface ICopilotGreetingProps {
  /** 当前 Tab 类型，决定渲染哪套问候语 */
  tab: 'aiAgent' | 'dsReport' | 'aiExcel' | 'magicPoster';
  /** 自定义问候语，传入后将覆盖默认的问候语（用于 aiAgent Tab 的 greetingNode 场景） */
  greetingNode?: React.ReactNode;
  /** 自定义图片，传入后将覆盖默认的图片（用于 aiAgent Tab 的 imgSrc 场景） */
  imgSrc?: string;
}

/**
 * Copilot 各 Tab 统一问候语组件
 * 根据 tab 属性映射不同的问候语内容，避免各 Tab 组件重复维护相同的 UI 结构
 */
export const CopilotGreeting: React.FC<ICopilotGreetingProps> = ({ tab, greetingNode, imgSrc }) => {
  const { locale } = useIntl();
  const isEn = locale === 'en-US';

  const defaultGreeting = useMemo(() => {
    switch (tab) {
      case 'aiAgent':
        return isEn ? (
          <>
            I am&nbsp;<span className="dbh-agent-blue-text">Brainy</span>, your
            dedicated game data AI expert. Whether it&apos;s{' '}
            <span className="dbh-agent-blue-text">data queries, competitor insights</span>
            , or{' '}
            <span className="dbh-agent-blue-text">report generation</span>,
            I&rsquo;m always available. <img src={Salute} alt="salute-icon" />
          </>
        ) : (
          <>
            我是&nbsp;<span className="dbh-agent-blue-text">Brainy</span>
            ，您的专属游戏数据AI专家。无论是
            <span className="dbh-agent-blue-text">数据查询、竞品洞察</span>
            还是<span className="dbh-agent-blue-text">报告生成</span>
            ，随时待命 <img src={Salute} alt="salute-icon" />
          </>
        );

      case 'aiExcel':
        return isEn ? (
          <>
            Hi, I&rsquo;m <span className="dbh-agent-blue-text">Brainy</span>, your
            dedicated AI expert for gaming data. Whether you need{' '}
            <span className="dbh-agent-blue-text">data queries, competitor insights</span>
            , or{' '}
            <span className="dbh-agent-blue-text">report generation</span>,
            I&rsquo;m always on standby.
          </>
        ) : (
          <>
            我是 <span className="dbh-agent-blue-text">Brainy</span>
            ，您的专属游戏数据AI专家。无论是
            <span className="dbh-agent-blue-text">数据查询、竞品洞察</span>
            还是<span className="dbh-agent-blue-text">报告生成</span>，随时待命。
          </>
        );

      case 'dsReport':
        return isEn ? (
          <>
            I am{' '}
            <span className="dbh-agent-blue-text">DataScience Report Assistant</span>
            , intelligently integrating content, flexible editing, one-click export
            of data reports for quick decision-making!{' '}
            <img src={Salute} alt="salute-icon" />
          </>
        ) : (
          <>
            我是<span className="dbh-agent-blue-text">DataScience专业报告生成助手</span>
            ，智能整合内容，灵活编辑，一键导出数据报告，快速决策！{' '}
            <img src={Salute} alt="salute-icon" />
          </>
        );

      case 'magicPoster':
        return isEn ? (
          <>
            I&rsquo;m the{' '}
            <span className="dbh-agent-blue-text">Data Magic Poster Assistant</span>
            , intelligently integrating content and exporting data posters with just
            one click!
          </>
        ) : (
          <>
            我是<span className="dbh-agent-blue-text">数据魔法海报生成助手</span>
            ，智能整合内容，一键导出数据海报！
          </>
        );

      default:
        return null;
    }
  }, [tab, isEn]);

  return (
    <div className="dbh-agent-landing-page">
      <div className="dbh-agent-cover-container">
        <BasicImage
          src={imgSrc || ''}
          alt={'answer icon'}
          errorSrc={AnswerIcon}
        />
        <Card.Body className="dbh-agent-hello">
          {greetingNode || defaultGreeting}
        </Card.Body>
      </div>
    </div>
  );
};

export default CopilotGreeting;
